import type { SxProps, Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

/** Only real hover (mouse / trackpad); avoids sticky :hover on touch after tap changing background opacity */
const hoverCapable = '@media (hover: hover) and (pointer: fine)';

/** Outlined icon buttons in slot expansion strips — use action palette, not secondary.main */
export const slotInlineOutlinedActionSx: SxProps<Theme> = {
  'color': 'action.active',
  'border': '1px solid',
  'borderColor': 'action.active',
  'boxShadow': 'none',
  'outline': 'none',
  'WebkitTapHighlightColor': 'transparent',
  [hoverCapable]: {
    '&:hover': {
      borderColor: 'action.active',
      backgroundColor: 'action.hover',
      boxShadow: 'none',
    },
    '&&:hover:focus': {
      borderColor: 'action.active !important',
      backgroundColor: 'action.hover !important',
      boxShadow: 'none !important',
    },
  },
  // Mouse click leaves :focus; MUI :focus can differ from unfocused first paint
  '&&:focus': {
    color: 'action.active !important',
    border: '1px solid !important',
    borderColor: 'action.active !important',
    backgroundColor: 'transparent !important',
    boxShadow: 'none !important',
  },
  '&&:active, &&.Mui-focusVisible': {
    color: 'action.active !important',
    border: '1px solid !important',
    borderColor: 'action.active !important',
    backgroundColor: 'action.hover !important',
    boxShadow: 'none !important',
  },
};

const contrastOnPrimary = (theme: Theme) => theme.palette.getContrastText(theme.palette.text.primary);
const contrastOnSecondary = (theme: Theme) => theme.palette.getContrastText(theme.palette.text.secondary);

/**
 * Contained “selected” state (fixed time on, active tab).
 * `color="inherit"` + contained applies extra focus/active rules from MUI — use !important so
 * post-click :focus matches the unfocused selected look.
 */
export function slotInlineContainedActionSx(theme: Theme): SystemStyleObject<Theme> {
  const primary = theme.palette.text.primary;
  const secondary = theme.palette.text.secondary;
  const onPrimary = contrastOnPrimary(theme);
  const onSecondary = contrastOnSecondary(theme);
  return {
    'bgcolor': `${primary} !important`,
    'backgroundColor': `${primary} !important`,
    'color': `${onPrimary} !important`,
    'border': `1px solid ${primary} !important`,
    'borderColor': `${primary} !important`,
    'boxShadow': 'none !important',
    'outline': 'none',
    'WebkitTapHighlightColor': 'transparent',
    [hoverCapable]: {
      '&:hover': {
        bgcolor: `${secondary} !important`,
        backgroundColor: `${secondary} !important`,
        color: `${onSecondary} !important`,
        borderColor: `${secondary} !important`,
        boxShadow: 'none !important',
      },
      '&&:hover:focus': {
        bgcolor: `${secondary} !important`,
        backgroundColor: `${secondary} !important`,
        color: `${onSecondary} !important`,
        borderColor: `${secondary} !important`,
        boxShadow: 'none !important',
      },
      '&&:hover:active': {
        bgcolor: `${secondary} !important`,
        backgroundColor: `${secondary} !important`,
        color: `${onSecondary} !important`,
        borderColor: `${secondary} !important`,
        boxShadow: 'none !important',
      },
      '&&:hover.Mui-focusVisible': {
        bgcolor: `${secondary} !important`,
        backgroundColor: `${secondary} !important`,
        color: `${onSecondary} !important`,
        borderColor: `${secondary} !important`,
        boxShadow: 'none !important',
      },
    },
    '&&:focus': {
      bgcolor: `${primary} !important`,
      backgroundColor: `${primary} !important`,
      color: `${onPrimary} !important`,
      border: `1px solid ${primary} !important`,
      borderColor: `${primary} !important`,
      boxShadow: 'none !important',
    },
    '&&:active': {
      bgcolor: `${primary} !important`,
      backgroundColor: `${primary} !important`,
      color: `${onPrimary} !important`,
      border: `1px solid ${primary} !important`,
      borderColor: `${primary} !important`,
      boxShadow: 'none !important',
    },
    '&&.Mui-focusVisible': {
      bgcolor: `${primary} !important`,
      backgroundColor: `${primary} !important`,
      color: `${onPrimary} !important`,
      border: `1px solid ${primary} !important`,
      borderColor: `${primary} !important`,
      boxShadow: 'none !important',
    },
  };
}
